import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { ADVANCED_ORDER_TYPE_LABELS } from '@/constants/advancedOrder';
import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { fontStyles } from '@/styles/typography';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';

import type { LimitOrderItem } from '@/utils/0x.types';
import { getLimitOrderDisplayBuyAmount } from '@/utils/advancedOrderFees';

import { CancelOrderButton } from '../cells/CancelOrderButton';
import { OrderStatusCell } from '../TableCell/OrderStatusCell';
import { TokenAmountCell } from '../TableCell/TokenAmountCell';
import { TokenDateCell } from '../TableCell/TokenDateCell';
import { getLimitOrderDisplayPrice } from '../utils';

/**
 * Per-column responsive visibility shared by open-order tables, applied to both
 * the header and body cell of each column so they stay in sync. The history
 * page uses this directly so per-row cancellation remains available on mobile.
 */
export const OPEN_ORDER_COLUMN_CLASS: Record<string, string> = {
  type: 'hidden tablet:table-cell',
  expiry: 'hidden desktop:table-cell',
  network: 'hidden tablet:table-cell',
  status: 'hidden tablet:table-cell',
  actions: 'text-right',
};

function OpenOrderNetworkCell({ chainId }: { chainId: number }) {
  return (
    <span className="flex items-center">
      <ChainAvatar chainId={chainId} alt={`${NETWORK_NAME_PER_CHAIN_ID[chainId]} logo`} size={16} />
      <span className="ml-2">{NETWORK_NAME_PER_CHAIN_ID[chainId]}</span>
    </span>
  );
}

const columnHelper = createColumnHelper<LimitOrderItem>();

type OpenOrderColumnsOptions = {
  /** Show the Price column. */
  showPrice?: boolean;
};

/**
 * Builds the column defs for an open-orders table. The Sell/Buy amounts come
 * straight off the `LimitOrderItem` (maker = sell, taker = buy).
 */
export function getOpenOrderColumns({
  showPrice = false,
}: OpenOrderColumnsOptions = {}): ColumnDef<LimitOrderItem>[] {
  const columns: ColumnDef<LimitOrderItem>[] = [];

  columns.push(
    columnHelper.display({
      id: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <span className={fontStyles({ variant: 'BODY_MD' })}>
          {ADVANCED_ORDER_TYPE_LABELS[row.original.advancedOrderType ?? 'limit']}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'sell',
      header: 'Sell',
      cell: ({ row }) => (
        <TokenAmountCell amount={row.original.makerAmount} token={row.original.makerTokenResult} />
      ),
    }),
    columnHelper.display({
      id: 'buy',
      header: 'Buy',
      cell: ({ row }) => {
        const { amount, isApproximate } = getLimitOrderDisplayBuyAmount(row.original);
        return (
          <TokenAmountCell
            amount={amount}
            token={row.original.takerTokenResult}
            isApproximate={isApproximate}
          />
        );
      },
    }),
  );

  if (showPrice) {
    columns.push(
      columnHelper.display({
        id: 'price',
        header: 'Price',
        cell: ({ row }) => getLimitOrderDisplayPrice(row.original),
      }),
    );
  }

  columns.push(
    columnHelper.display({
      id: 'expiry',
      header: 'Expiration',
      cell: ({ row }) => <TokenDateCell item={row.original} />,
    }),
    columnHelper.display({
      id: 'network',
      header: 'Network',
      cell: ({ row }) => <OpenOrderNetworkCell chainId={row.original.chainId} />,
    }),
    columnHelper.display({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <OrderStatusCell item={row.original} />,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <CancelOrderButton item={row.original} />,
    }),
  );

  return columns;
}
